import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider, useT } from "./i18n";
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import AddItem from "./pages/AddItem";
import Suggestions from "./pages/Suggestions";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import PendingApproval from "./pages/PendingApproval";
import ChoosePlan from "./pages/ChoosePlan";
import SplashScreen from "./components/SplashScreen";
import LanguagePickerDrawer from "./components/LanguagePickerDrawer";
import InstallAppPrompt from "./components/InstallAppPrompt";
import { ConfirmDialogProvider } from "./context/ConfirmDialog";

const SPLASH_SESSION_KEY = "niper_splash_shown";

function isAccountBlocked(u) {
  if (!u || u.is_super_admin) return false;
  if (u.is_active === false) return true;
  if (u.activated_until) {
    try { if (new Date(u.activated_until) < new Date()) return true; } catch (_e) { /* noop */ }
  }
  return false;
}

function Protected({ children, requireHousehold = true, requirePlan = true }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoading />;
  if (!user) return <Navigate to="/" replace />;
  if (isAccountBlocked(user)) return <Navigate to="/pending" replace />;
  if (requirePlan && !user.is_super_admin && !user.account_type) return <Navigate to="/choose-plan" replace />;
  if (requireHousehold && !user.household_id) return <Navigate to="/onboarding" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoading />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoading />;
  if (user) {
    if (isAccountBlocked(user)) return <Navigate to="/pending" replace />;
    if (!user.is_super_admin && !user.account_type) return <Navigate to="/choose-plan" replace />;
    if (!user.household_id) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function FullScreenLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="full-loading">
      <div className="text-zinc-400 text-sm animate-pulse">Loading…</div>
    </div>
  );
}

function GlobalLanguageGate() {
  const { needsChoice } = useT();
  return <LanguagePickerDrawer open={needsChoice} forced onOpenChange={() => {}} />;
}

function AppShell({ children }) {
  // Show the branded splash the first time the tab loads (per session).
  const [splash, setSplash] = useState(() => {
    try { return !sessionStorage.getItem(SPLASH_SESSION_KEY); } catch (_e) { return true; }
  });
  useEffect(() => {
    if (!splash) return;
    try { sessionStorage.setItem(SPLASH_SESSION_KEY, "1"); } catch (_e) { /* noop */ }
  }, [splash]);
  return (
    <>
      {splash && <SplashScreen onDone={() => setSplash(false)} />}
      <GlobalLanguageGate />
      <InstallAppPrompt />
      {children}
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/choose-plan" element={<Protected requireHousehold={false} requirePlan={false}><ChoosePlan /></Protected>} />
      <Route path="/onboarding" element={<Protected requireHousehold={false}><Onboarding /></Protected>} />
      <Route path="/pending" element={<PendingApproval />} />
      <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/add" element={<Protected><AddItem /></Protected>} />
      <Route path="/suggestions" element={<Protected><Suggestions /></Protected>} />
      <Route path="/settings" element={<Protected requireHousehold={false}><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <div className="mobile-shell">
        <BrowserRouter>
          <I18nProvider>
            <AuthProvider>
              <ConfirmDialogProvider>
                <AppShell>
                  <AppRouter />
                </AppShell>
                <Toaster position="top-center" richColors closeButton />
              </ConfirmDialogProvider>
            </AuthProvider>
          </I18nProvider>
        </BrowserRouter>
      </div>
    </div>
  );
}
